"use client"

import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { blobMediaUrl } from "@/lib/blob-url"
import { StepsCardRow } from "@/components/ui/steps-card-row"
import { MRI_WALKTHROUGH_STEPS, mriStepForSite } from "@/data/mri-sections"
import { HomeV4Close } from "../home/home-v4-close"
import { HomeV4Compare } from "../home/home-v4-compare"
import { BookButton, usePlanPrices } from "./pricing-v5-shared"

/**
 * The `/go/fullbodymri` landing's own sections, the MRI's take on the blood
 * landing's (Eugene, 08.10). Copy in `tests` (steps), `go` (couples) and
 * `pricingV5` (CTA and close).
 */

/** The walkthrough's photos for the four landing steps: the visit, the scan,
 *  the radiologists and the doctor's call. The report step folds into the
 *  last one. */
const STEP_PHOTOS = [0, 1, 2, 4].map(
  (i) => mriStepForSite(MRI_WALKTHROUGH_STEPS[i], true).image
)

/** The check in a bright green circle, as in the blood landing's offer. */
function CheckCircle() {
  return (
    <span className="bg-bright-green-200 flex size-9 shrink-0 items-center justify-center rounded-full text-neutral-900">
      <svg aria-hidden viewBox="0 0 24 24" className="size-5">
        <path
          d="M5.5 12.5l4.25 4.25L18.5 8"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

/** How it goes in four cards side by side, as the blood landing's three,
 *  in place of the five-step scroll walkthrough. */
export function MriLandingSteps({
  trackingLocation,
}: {
  trackingLocation: string
}) {
  const tt = useTranslations("tests")
  const t = useTranslations("pricingV5")
  return (
    <div className="bg-white">
      <div className="mx-auto max-w-[1440px]">
        <StepsCardRow
          eyebrow={tt("mriWalkLabel")}
          heading={tt("mriWalkHeading")}
          steps={([1, 2, 3, 4] as const).map((n) => ({
            id: `landingMriStep${n}`,
            title: tt(`landingMriStep${n}Title`),
            body: tt(`landingMriStep${n}Body`),
            media: STEP_PHOTOS[n - 1] ? (
              <Image
                src={blobMediaUrl(STEP_PHOTOS[n - 1]!)}
                alt=""
                fill
                sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 80vw"
                quality={90}
                className="object-cover"
              />
            ) : null,
          }))}
          cta={
            <BookButton
              scan="mri"
              label={t("mriLandingCta")}
              trackingLabel="go_fullbodymri_steps_cta"
              trackingLocation={trackingLocation}
              size="lg"
              arrow={false}
              buttonClassName="md:px-10"
            />
          }
        />
      </div>
    </div>
  )
}

/** The old ad page's couples offer, kept on the landing (Eugene, 08.10). */
export function MriCouples({ trackingLocation }: { trackingLocation: string }) {
  const t = useTranslations("go")
  return (
    <section className="bg-white px-4 py-16 md:px-10 md:py-24">
      <div className="mx-auto grid max-w-[1040px] grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
        <div>
          <h2 className="type-display-sm-emphasized text-balance text-neutral-900">
            {t("couplesHeading")}
          </h2>
          <p className="type-body-lg mt-4 text-neutral-600">
            {t("couplesBody")}
          </p>
          <ul className="mt-8 flex flex-col gap-4">
            {(
              ["couplesBullet1", "couplesBullet2", "couplesBullet3"] as const
            ).map((key) => (
              <li key={key} className="flex items-center gap-3">
                <CheckCircle />
                <span className="type-body-lg text-neutral-900">{t(key)}</span>
              </li>
            ))}
          </ul>
          <BookButton
            scan="mri"
            label={t("couplesCta")}
            trackingLabel="Couples book MRI"
            trackingLocation={trackingLocation}
            size="lg"
            arrow={false}
            className="mt-10 block w-full sm:w-auto"
            buttonClassName="w-full md:px-10"
          />
        </div>
        <div className="relative aspect-square overflow-hidden rounded-[24px]">
          <Image
            src={blobMediaUrl("/images/go/couples.jpg")}
            alt=""
            fill
            sizes="(min-width: 768px) 480px, 100vw"
            className="object-cover"
          />
        </div>
      </div>
    </section>
  )
}

/** The landing's closing block: the homepage's photo band, asking for the
 *  scan's booking rather than the quiz, as the blood landing's does. */
export function MriClose({ trackingLocation }: { trackingLocation: string }) {
  const t = useTranslations("pricingV5")
  return (
    <HomeV4Close
      section="mx-auto max-w-[1440px] px-5 md:px-10"
      lede={t("mriCloseLede")}
      results={t("mriCloseResults")}
      book={{
        heading: {
          plain: `${t("mriCloseHeadingPlain")} `,
          accent: t("mriCloseHeadingAccent"),
        },
        cta: (
          <BookButton
            scan="mri"
            label={t("mriLandingCta")}
            trackingLabel="go_fullbodymri_close_cta"
            trackingLocation={trackingLocation}
            size="lg"
            arrow={false}
            className="block w-full sm:w-auto"
            buttonClassName="w-full bg-white px-8 text-neutral-950 hover:bg-neutral-100 hover:shadow-none md:px-10"
          />
        ),
      }}
    />
  )
}

/** The landing's offer under the finds cards, as the blood landing's: what
 *  you get in one line, three points and the Advanced package's booking. */
export function MriOfferSection({
  trackingLocation,
}: {
  trackingLocation: string
}) {
  const t = useTranslations("pricingV5")
  return (
    <section
      aria-labelledby="mri-offer-heading"
      className="bg-white px-4 py-16 md:px-10 md:py-24"
    >
      <div className="mx-auto flex max-w-[760px] flex-col items-center text-center">
        <h2
          id="mri-offer-heading"
          className="type-display-sm-emphasized text-balance text-neutral-900"
        >
          {t("mriOfferHeading")}
        </h2>
        <ul className="mt-10 grid w-full max-w-[680px] grid-cols-1 gap-6 md:mt-12 md:grid-cols-3 md:gap-4">
          {([1, 2, 3] as const).map((n) => (
            <li key={n} className="flex flex-col items-center gap-3">
              <span className="bg-bright-green-200 flex size-11 items-center justify-center rounded-full text-neutral-900">
                <svg aria-hidden viewBox="0 0 24 24" className="size-6">
                  <path
                    d="M5.5 12.5l4.25 4.25L18.5 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="type-body-lg max-w-[210px] text-balance text-neutral-900">
                {t(`mriOfferPoint${n}`)}
              </p>
            </li>
          ))}
        </ul>
        <BookButton
          scan="mri"
          label={t("mriLandingCta")}
          trackingLabel="go_fullbodymri_offer_cta"
          trackingLocation={trackingLocation}
          size="lg"
          arrow={false}
          className="mt-10 block w-full sm:w-auto md:mt-12"
          buttonClassName="w-full md:px-10"
        />
        <p className="type-body-md mt-4 max-w-[520px] text-balance text-neutral-600">
          {t("mriOfferNote")}
        </p>
      </div>
    </section>
  )
}

/** The homepage's compare table, turned to the scan for the landing. Needs
 *  MriPlanScope for the plan price the home button would show. */
export function MriCompare({ trackingLocation }: { trackingLocation: string }) {
  const t = useTranslations("pricingV5")
  const { money, tier } = usePlanPrices()
  return (
    <HomeV4Compare
      section="mx-auto max-w-[1440px] px-5 md:px-10"
      planPrice={money(tier("once"))}
      mri
      cta={
        <BookButton
          scan="mri"
          label={t("mriLandingCta")}
          trackingLabel="go_fullbodymri_compare_cta"
          trackingLocation={trackingLocation}
          size="lg"
          arrow={false}
          className="block w-full sm:w-auto"
          buttonClassName="w-full md:px-10"
        />
      }
    />
  )
}
